import type { AnimalDefinition } from './types';

export interface CenterOfMassBodyApi {
  setCentre(body: MatterJS.BodyType, centre: MatterJS.Vector, relative?: boolean): void;
}

export interface AppliedCenterOfMass {
  mode: 'auto' | 'authored';
  point: { x: number; y: number };
}

const preservedAimOffsets = new WeakMap<object, { x: number; y: number }>();

function pointOnSegment(
  point: MatterJS.Vector,
  start: MatterJS.Vector,
  end: MatterJS.Vector,
): boolean {
  const cross = (point.y - start.y) * (end.x - start.x) - (point.x - start.x) * (end.y - start.y);
  if (Math.abs(cross) > 1e-7) return false;
  const dot = (point.x - start.x) * (end.x - start.x) + (point.y - start.y) * (end.y - start.y);
  if (dot < 0) return false;
  const squaredLength = (end.x - start.x) ** 2 + (end.y - start.y) ** 2;
  return dot <= squaredLength;
}

export function pointInConvexHull(point: MatterJS.Vector, vertices: readonly MatterJS.Vector[]): boolean {
  if (vertices.length < 3) return false;
  let inside = false;
  for (let index = 0, previous = vertices.length - 1; index < vertices.length; previous = index++) {
    const start = vertices[previous]!;
    const end = vertices[index]!;
    if (pointOnSegment(point, start, end)) return true;
    const crosses = (end.y > point.y) !== (start.y > point.y)
      && point.x < ((start.x - end.x) * (point.y - end.y)) / (start.y - end.y) + end.x;
    if (crosses) inside = !inside;
  }
  return inside;
}

export function sourceNormalizedToBodyPoint(
  animal: AnimalDefinition,
  sourceNormalized: { x: number; y: number },
): { x: number; y: number } {
  const visualCenter = animal.visualCenter ?? { x: 0, y: 0 };
  return {
    x: visualCenter.x + (sourceNormalized.x - 0.5) * animal.width,
    y: visualCenter.y + (sourceNormalized.y - 0.5) * animal.height,
  };
}

export function applyAnimalCenterOfMass(
  bodyApi: CenterOfMassBodyApi,
  body: MatterJS.BodyType,
  animal: AnimalDefinition,
): AppliedCenterOfMass {
  if (animal.centerOfMass.mode === 'auto') {
    preservedAimOffsets.set(body, { x: 0, y: 0 });
    return { mode: 'auto', point: { x: body.position.x, y: body.position.y } };
  }

  const automaticPoint = { x: body.position.x, y: body.position.y };
  const point = sourceNormalizedToBodyPoint(animal, animal.centerOfMass.sourceNormalized);
  if (!body.vertices || !pointInConvexHull(point, body.vertices)) {
    throw new Error(`Authored center of mass for ${animal.id} is outside the Matter convex hull`);
  }
  bodyApi.setCentre(body, point, false);
  preservedAimOffsets.set(body, {
    x: point.x - automaticPoint.x,
    y: point.y - automaticPoint.y,
  });
  return { mode: 'authored', point };
}

export function centerOfMassAimOffset(body: MatterJS.BodyType): { x: number; y: number } {
  return preservedAimOffsets.get(body) ?? { x: 0, y: 0 };
}

export function bodyPositionForPreservedAimAnchor(
  anchor: { x: number; y: number },
  localOffset: { x: number; y: number },
  angleRadians: number,
): { x: number; y: number } {
  const cosine = Math.cos(angleRadians);
  const sine = Math.sin(angleRadians);
  return {
    x: anchor.x + localOffset.x * cosine - localOffset.y * sine,
    y: anchor.y + localOffset.x * sine + localOffset.y * cosine,
  };
}

export function preservedAimOrigin(
  appliedOrigin: { x: number; y: number },
  localOffset: { x: number; y: number },
  dimensions: { width: number; height: number },
): { x: number; y: number } {
  return {
    x: appliedOrigin.x - localOffset.x / dimensions.width,
    y: appliedOrigin.y - localOffset.y / dimensions.height,
  };
}

export function centerOfMassDebugPoint(body: MatterJS.BodyType, animal: AnimalDefinition) {
  const mode = animal.centerOfMass.mode;
  return {
    mode,
    point: { x: body.position.x, y: body.position.y },
    color: mode === 'authored' ? 0xf04444 : 0xffd43b,
    label: mode === 'authored' ? 'COM AUTHORED' : 'COM AUTO',
  } as const;
}
